import React, { useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import toast from "react-hot-toast";
import * as yup from "yup";
import { CreateOrganization, UpdateOrganization, GetOrganizationById, GetAdminUsers, getAllEmsOffices } from "../../../../../api";
import ConfirmationModal from "../../../application/components/ConfirmationModal/ConfirmationModal";
import InfoIcon from "../../../../assets/svg/icons/common/info";
import PercentIcon from "../../../../assets/svg/icons/common/percent";
import FancyRadioInput from "../../../../components/form/fancy-radio-input";
import Select from "../../../../components/form/select";
import Switch from "../../../../components/form/switch";
import TextInput from "../../../../components/form/text-input";
import formBaseStyles from "../../../../components/form/text-input/styles.module.scss";
import HeadingWithLine from "../../../../components/HeadingWithLine/HeadingWithLine";
import Modal from "../../../../components/modal";
import Button from "../../../../components/new-button";
import AddLogo from "./components/add-logo";
import styles from "./styles.module.scss";
import { services } from "../../../../../constants";
import { TagInput } from "../../../../components/form/tag-input";
import useOrganizationStore from "../../../../stores/organization";
import useUserStore from "../../../../stores/user";
import AddressAutocomplete from "../../../../components/form/address-autocomplete";
import { googlePlaceToFormData } from "../../../../utils/address-parser";

const schema = yup.object({
  name: yup.string().trim().required("* Name is required"),
  whitelabel_slug: yup.string().trim().required("* Slug is required"),
  company_name: yup.string().trim().required("* Company Name is required").nullable(),
  email: yup.string().email("Invalid email address").required("* Email required"),
  phone: yup.string().required("* Phone required"),
  address: yup.string().required("* Address required"),
  low_risk: yup.string().nullable(),
  high_risk: yup.string().nullable(),
  rev_share_tiers: yup.string().nullable(),
  fileToUpload: yup.mixed().nullable(),
  smtp_enabled: yup.boolean(),
  smtp_server: yup.string().when("smtp_enabled", {
    is: 1,
    then: yup.string().required("* SMTP Server is required"),
  }),
  smtp_port: yup.string().when("smtp_enabled", {
    is: 1,
    then: yup.string().required("* SMTP Port is required"),
  }),
  smtp_username: yup.string().when("smtp_enabled", {
    is: 1,
    then: yup.string().required("* SMTP Username is required"),
  }),
  smtp_password: yup.string().when("smtp_enabled", {
    is: 1,
    then: yup.string().required("*SMTP Password is required"),
  }),
  smtp_encryption: yup.string().when("smtp_enabled", {
    is: 1,
    then: yup.string().required("* SMTP Encryption is required"),
  }),
});

const getOrganizationImageSrc = (slug) => {
  const count = localStorage.getItem("counter") ? localStorage.getItem("counter") : "";
  return slug ? `/img/${slug}/logo-solid.png?${count}` : '';
};

const normalizeOfficeTags = (offices) => {
  if (!Array.isArray(offices)) {
    return [];
  }

  return offices
    .map((office) => {
      if (typeof office === "string" || typeof office === "number") {
        return {
          label: String(office),
          value: String(office),
        };
      }

      if (office && typeof office === "object") {
        const label = office.label ?? office.value;
        const value = office.value ?? office.label;

        if (label && value) {
          return {
            label: String(label),
            value: String(value),
          };
        }
      }

      return null;
    })
    .filter(Boolean);
};

const mapOrganizationOfficesToOptions = (offices) => {
  if (!Array.isArray(offices)) {
    return [];
  }

  return offices
    .map((office) => {
      if (typeof office === "string" || typeof office === "number") {
        const value = String(office);
        return { label: value, value };
      }

      if (office && typeof office === "object") {
        const label = office.label ?? office.value;
        const value = office.value ?? office.label;

        if (label && value) {
          return {
            label: String(label),
            value: String(value),
          };
        }
      }

      return null;
    })
    .filter(Boolean);
};

// The Kurv Office HR / LR selects only offer offices of the matching risk type.
// An office the org already points at is kept in the list even when it no longer
// matches, so editing an org can't silently blank out an existing selection.
const officeOptionsForRisk = (offices, riskType, currentValue) => {
  const matching = offices.filter((office) => office.risk_type === riskType);
  const current =
    currentValue === null || currentValue === undefined || currentValue === ""
      ? ""
      : String(currentValue);

  if (!current || matching.some((office) => office.value === current)) {
    return matching;
  }

  const saved = offices.find((office) => office.value === current);

  return [saved ?? { label: current, value: current, risk_type: null }, ...matching];
};

export const AddOrganizationModal = ({
  open,
  onClose,
  isEdit = false,
  // isPcAdmin,
  // emsOffices,
  // organization_user,
  // selectedOrganization,
  // relationShipManagers,
}) => {
  const [errors, setErrors] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    main_contact: "",
    main_admin: "",
    whitelabel_slug: "",
    email: "",
    phone: "",
    address: "",
    high_risk: "",
    primary_contact_id: "",
    contract_negotiation: "",
    uw_id: "",
    type: "",
    low_risk: "",
    rev_share_tiers: "",
    ems_entity: "",
    bank_name: "",
    routing_number: "",
    account_number: "",
    contact_preference: "",
    ems_office_hr: "",
    ems_office_lr: "",
    update_existing_lead_uw_id: "",
    smtp_server: "",
    smtp_port: "",
    smtp_username: "",
    smtp_password: "",
    smtp_encryption: "",
    ach_info_for_billing: 0,
    smtp_enabled: 0,
    enable_agent_training: 0,
    show_create: 0,
    beta_features: 0,
    pricing_restricted: 0,
    additional_team_lead: [],
    referral_partner: 0,
    sync_with_kurv: 0,
    enable_personal_guarantor_toggle: 0,
    offices: [],
  });

  const [organization_user, setOrganizationUser] = useState([]);
  const [orgUserOptions, setOrgUserOptions] = useState([]);
  const [relationShipManagers, setRelationShipManagers] = useState([]);
  const [kurvRelationShipManagers, setKurvRelationShipManagers] = useState([]);
  const [emsOffices, setEmsOffices] = useState([]);
  const [selectedOrgData, setSelectedOrgData] = useState(null);

  const editOrganization = useOrganizationStore(
    (state) => state?.editOrganization,
  );

  const setEditOrganization = useOrganizationStore(
    (state) => state?.setEditOrganization,
  );

  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationId,
  );

  const setAddOrganizationModalOpen = useOrganizationStore(
    (state) => state?.setAddOrganizationModalOpen,
  );
  const setSelectedOrganizationData = useOrganizationStore(
    (state) => state?.setSelectedOrganizationData,
  );
  const setEMSOffices = useOrganizationStore((state) => state?.setEMSOffices);

  const isPCAdmin = useUserStore((state) => state.isPCAdmin);
  const isEntitySuperAdmin = useUserStore((state) => state.isEntitySuperAdmin);

  const [is_submitted, setIsSubmitted] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const formikRef = useRef(null);

  const closeModal = () => {
    setAddOrganizationModalOpen(false);
    setEditOrganization(false);
    onClose();
  };

  const handleBackdropClose = () => {
    if (formikRef.current?.dirty) {
      setShowDiscardConfirm(true);
    } else {
      closeModal();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false);
    closeModal();
  };

  useEffect(() => {
    const fetchAdminUsers = async () => {
      const adminUsersResponse = await GetAdminUsers();
      if (!adminUsersResponse?.success) {
        setRelationShipManagers([]);
        setKurvRelationShipManagers([]);
        return;
      }

      const adminUsers = adminUsersResponse?.users ?? [];
      const adminUserOptions = adminUsers.map((user, index) => {
        return {
          label: user.name,
          value: user.id,
        };
      });
      setRelationShipManagers(adminUserOptions);
      
      const kurvUsers = adminUsersResponse?.kurv_users ?? [];
      if (kurvUsers.length > 0) {
        const kurvRelationShipManagers = kurvUsers.map((user, index) => {
          return {
            label: user.name,
            value: user.id,
          };
        });
        // merge kurvRelationShipManagers and adminUserOptions
        const mergedRelationShipManagers = [...adminUserOptions, { label: "KurvPay", value: "kurvpay", divider: true }, ...kurvRelationShipManagers];
        setKurvRelationShipManagers(mergedRelationShipManagers);
      } else {
        setKurvRelationShipManagers(adminUserOptions);
      }

      // get ems offices
      try {
        // Needs the complete list (not paginated) — see officeOptionsForRisk().
        const emsOfficesResponse = await getAllEmsOffices();
        if (emsOfficesResponse?.status) {
          const officesTemp = emsOfficesResponse?.data.map((office) => ({
            value: office?.value,
            label: office?.value,
            risk_type: office?.risk_type ?? null,
          }));
          setEmsOffices(officesTemp);
        }
      } catch (err) {
        console.error("Error fetching EMS offices:", err);
      }
    };
    fetchAdminUsers();
  }, []);

  useEffect(() => {
    if (selectedOrganizationId && isEdit) {
      const fetchData = async () => {
        try {
          const organizationResponse = await GetOrganizationById(
            selectedOrganizationId,
          );
          const orgUsers = organizationResponse.users;
          const orgUserOptions = orgUsers.map((user, index) => {
            return {
              label: user.name,
              value: user.id,
            };
          });
          setOrgUserOptions(orgUserOptions);
          setOrganizationUser(orgUsers);

          const organization = organizationResponse.organization;
          setSelectedOrgData(organization);

          setFormData({
            ...formData,
            name: organization.name,
            company_name: organization.company_name,
            main_contact: organization.main_contact,
            main_admin: organization.main_admin,
            whitelabel_slug: organization.whitelabel_slug,
            email: organization.email,
            phone: organization.phone,
            address: organization.address,
            high_risk: organization.high_risk,
            bank_name: organization.bank_name,
            routing_number: organization.routing_number,
            account_number: organization.account_number,
            contact_preference: organization.contact_preference,
            ems_entity: organization.ems_entity,
            ems_office_hr: organization.ems_office_hr,
            ems_office_lr: organization.ems_office_lr,
            smtp_server: organization.smtp_server
              ? organization.smtp_server
              : "",
            smtp_port: organization.smtp_port ? organization.smtp_port : "",
            smtp_username: organization.smtp_username
              ? organization.smtp_username
              : "",
            smtp_password: organization.smtp_password
              ? organization.smtp_password
              : "",
            smtp_encryption: organization.smtp_encryption
              ? organization.smtp_encryption
              : "",
            smtp_enabled: organization.smtp_enabled,
            enable_agent_training: organization.enable_agent_training,
            show_create: organization.show_create,
            beta_features: organization.beta_features,
            ach_info_for_billing: organization.ach_info_for_billing,
            primary_contact_id: organization.primary_contact_id ?? "",
            contract_negotiation: organization.contract_negotiation ?? "",
            uw_id: organization.uw_id ?? "",
            type: organization.type,
            low_risk: organization.low_risk,
            rev_share_tiers: organization.rev_share_tiers,
            pricing_restricted: organization.pricing_restricted,
            additional_team_lead: organization.additional_team_lead,
            referral_partner: organization.referral_partner,
            sync_with_kurv: organization.sync_with_kurv,
            enable_personal_guarantor_toggle:
              organization.enable_personal_guarantor_toggle,
            offices: normalizeOfficeTags(organization.offices),
          });
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };

      fetchData();
    }
  }, [selectedOrganizationId, isEdit]);

  const handleOrganizationFormSubmit = (values, { setSubmitting }) => {
    const toastId = toast.loading(`${isEdit ? "Updating" : "Creating"} organization...`);
    let fd = new FormData();

    // Helper function to append values with proper type conversion
    const appendValue = (
      key,
      value,
      convertToInt = false,
      convertToJson = false,
    ) => {
      if (value !== undefined && value !== null && value !== "") {
        if (convertToInt) {
          fd.append(key, parseInt(value));
        } else if (convertToJson) {
          fd.append(key, JSON.stringify(value));
        } else {
          fd.append(key, value);
        }
      }
    };

    // Helper function to convert boolean values
    const appendBooleanValue = (key, value) => {
      fd.append(key, value === 1 ? "true" : "false");
    };

    // Basic organization information
    appendValue("main_admin", values?.main_admin?.value ?? values.main_admin);
    appendValue("name", values.name);
    appendValue("company_name", values.company_name);
    appendValue("main_contact", values.main_contact);
    appendValue("whitelabel_slug", values.whitelabel_slug);
    appendValue("email", values.email);
    appendValue("phone", values.phone);
    appendValue("address", values.address);
    appendValue("high_risk", values.high_risk);
    appendValue("low_risk", values.low_risk);
    appendValue("rev_share_tiers", values.rev_share_tiers);
    appendValue("type", values.type);

    // Contact and user IDs
    appendValue("primary_contact_id", values.primary_contact_id, true);
    appendValue(
      "additional_team_lead",
      values.additional_team_lead,
      false,
      true,
    );

    appendValue("contract_negotiation", values.contract_negotiation, true);
    appendValue("uw_id", values.uw_id, true);

    // Bank details
    appendValue("bank_name", values.bank_name);
    appendValue("routing_number", values.routing_number);
    appendValue("account_number", values.account_number);
    appendValue("contact_preference", values.contact_preference);

    // Processors and offices
    appendValue("ems_entity", values.ems_entity);
    appendValue("ems_office_hr", values.ems_office_hr);
    appendValue("ems_office_lr", values.ems_office_lr);
    appendValue("offices", values.offices, false, true);
    appendValue(
      "update_existing_lead_uw_id",
      values.update_existing_lead_uw_id,
    );

    // SMTP settings - only append if SMTP is enabled
    if (values.smtp_enabled) {
      appendValue("smtp_enabled", values.smtp_enabled);
      appendValue("smtp_server", values.smtp_server);
      appendValue("smtp_port", values.smtp_port);
      appendValue("smtp_username", values.smtp_username);
      appendValue("smtp_password", values.smtp_password);
      appendValue("smtp_encryption", values.smtp_encryption);
    }

    // File upload
    if (values.fileToUpload) {
      fd.append("fileToUpload", values.fileToUpload);
    }

    // Boolean flags
    appendBooleanValue("beta_features", values.beta_features);
    appendBooleanValue("enable_agent_training", values.enable_agent_training);
    appendBooleanValue("show_create", values.show_create);
    appendBooleanValue("ach_info_for_billing", values.ach_info_for_billing);
    appendBooleanValue("pricing_restricted", values.pricing_restricted);
    appendBooleanValue("referral_partner", values.referral_partner);
    appendBooleanValue("sync_with_kurv", values.sync_with_kurv);
    appendBooleanValue(
      "enable_personal_guarantor_toggle",
      values.enable_personal_guarantor_toggle,
    );

    // API call based on whether we're editing or creating
    const apiCall = isEdit
      ? UpdateOrganization(selectedOrganizationId, fd)
      : CreateOrganization(fd);

    apiCall
      .then(async (response) => {
        setSubmitting(false);
        if (response.success) {
          if (isEdit && selectedOrganizationId) {
            try {
              const refreshedOrganizationResponse =
                await GetOrganizationById(selectedOrganizationId);
              if (refreshedOrganizationResponse?.success) {
                setSelectedOrganizationData(refreshedOrganizationResponse);
                setEMSOffices(
                  mapOrganizationOfficesToOptions(
                    refreshedOrganizationResponse?.organization?.offices,
                  ),
                );
              }
            } catch (refreshError) {
              console.error(
                "Failed to refresh organization details after update:",
                refreshError,
              );
            }
          }

          localStorage.setItem("counter", new Date().getTime());
          toast.success(
            `Organization ${isEdit ? "updated" : "created"} successfully`,
            { id: toastId, duration: 5000 },
          );
          onClose();
          setAddOrganizationModalOpen(false);
          setEditOrganization(false);
        } else {
          const errorMessage = response.message || "Unknown error";
          toast.error(
            `There was a problem ${isEdit ? "updating" : "creating"} organization: ${errorMessage}`,
            { id: toastId, duration: 5000 },
          );
          console.error(
            `${isEdit ? "Update" : "Create"} organization failed:`,
            response,
          );
        }
      })
      .catch((error) => {
        setSubmitting(false);
        toast.error(
          `There was a problem ${isEdit ? "updating" : "creating"} organization`,
          { id: toastId, duration: 5000 },
        );
        console.error(
          `Error ${isEdit ? "updating" : "creating"} organization:`,
          error,
        );
      });
  };

  // Add function to handle address selection from Google Place Autocomplete
  const handleBusinessAddressSelected = (place, setFieldValue) => {

    if (!place) return;

    // Parse the Google place data
    const addressData = googlePlaceToFormData(place, {
      streetAddress: 'address'
    });

    // Build combined string: address, city, state, country
    const combinedAddress = [
      addressData.address,
      addressData.city,
      addressData.state,
      addressData.country
    ]
      .filter(Boolean) // remove empty/null values
      .join(", ");

    // Override address with combined value

    setFieldValue("address", combinedAddress);
  };

  return (
    <>
    <Modal
      isOpen={open}
      onClose={closeModal}
      confirmClose={handleBackdropClose}
      header={`${isEdit ? "Edit" : "Add"} organization`}
      notCentered
      size="md"
    >
      <Formik
        innerRef={formikRef}
        enableReinitialize
        validationSchema={schema}
        onSubmit={handleOrganizationFormSubmit}
        initialValues={formData}
        // initialErrors={errors}
        validateOnBlur={false}
        validateOnChange={true}
        // ToDo: Come back to this later, not sure if we need this
        // validate={(values) => {
        //   setIsSubmitted(true);
        // }}
      >
        {({
          handleSubmit,
          handleChange,
          values,
          touched,
          errors,
          setFieldValue,
          isSubmitting,
        }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <AddLogo
              imageSrc={getOrganizationImageSrc(
                selectedOrgData?.whitelabel_slug,
              )}
            />

            <div className={`${styles.grid} ${styles.gap2}`}>
              <div
                className={`${styles.grid} ${styles.gridCols3} ${styles.gap2}`}
              >
                <TextInput
                  label="Organization name"
                  id="name"
                  name="name"
                  defaultValue={values.name}
                  onChange={handleChange}
                  touched={touched.name}
                  errored={errors.name}
                  error={errors["name"]}
                />

                <TextInput
                  label="Company name"
                  id="company_name"
                  name="company_name"
                  defaultValue={values.company_name}
                  onChange={handleChange}
                  touched={touched.company_name}
                  errored={errors.company_name}
                  error={errors["company_name"]}
                />

                <TextInput
                  label="Main contact"
                  id="main_contact"
                  name="main_contact"
                  defaultValue={values.main_contact}
                  onChange={handleChange}
                  touched={touched.main_contact}
                  errored={errors.main_contact}
                  error={errors["main_contact"]}
                />

                {isEdit && (
                  <Select
                    label="Admin user"
                    id="main_admin"
                    name="main_admin"
                    placeholder="Select user"
                    options={orgUserOptions}
                    users
                    search
                    value={values.main_admin}
                    onChange={(value) => {
                      setFieldValue("main_admin", value);
                    }}
                  />
                )}
                <TextInput
                  label="PCI Slug"
                  id="whitelabel_slug"
                  name="whitelabel_slug"
                  defaultValue={values.whitelabel_slug}
                  onChange={handleChange}
                  touched={touched.whitelabel_slug}
                  errored={errors.whitelabel_slug}
                  error={errors["whitelabel_slug"]}
                />
                <Select
                  label="Type"
                  placeholder="Select type"
                  id="type"
                  name="type"
                  options={[
                    { label: "Agent", value: "hands_on" },
                    {
                      label: "Referral Partner",
                      value: "hands_off",
                    },
                  ]}
                  value={values?.type}
                  onChange={(value) => {
                    const valueToSet = typeof value == 'object' ? value.value : value;
                    setFieldValue("type", valueToSet);
                  }}
                />
              </div>
              <div
                className={`${styles.grid} ${styles.gridCols1} ${styles.gap2}`}
              >
                {/* <TextInput
                  label="Business Address"
                  id="address"
                  name="address"
                  defaultValue={values.address}
                  onChange={handleChange}
                  touched={touched.address}
                  errored={errors.address}
                  error={errors["address"]}
                /> */}
                <AddressAutocomplete
                  label={"Business Address"}
                  placeholder={"Business Address"}
                  id="address"
                  name="address"
                  value={values.address}
                  onChange={handleChange}
                  onPlaceSelected={(place) => handleBusinessAddressSelected(place, setFieldValue)}
                  touched={touched.address}
                  errored={errors.address}
                  error={errors["address"]}
                />
              </div>
              <div
                className={`${styles.grid} ${styles.gridCols3} ${styles.gap2}`}
              >
                <TextInput
                  label="Business Phone Number"
                  id="phone"
                  name="phone"
                  defaultValue={values.phone}
                  touched={touched.phone}
                  errored={errors.phone}
                  error={errors["phone"]}
                  cleaveOptions={{
                    blocks: [3, 3, 4],
                    delimiter: "-",
                    numericOnly: true,
                    onValueChanged: (e) => {
                      setFieldValue("phone", e.target.value);
                    },
                  }}
                />

                <TextInput
                  label="Business Email"
                  id="email"
                  name="email"
                  defaultValue={values.email}
                  onChange={handleChange}
                  touched={touched.email}
                  errored={errors.email}
                  error={errors["email"]}
                />
                <Select
                  options={kurvRelationShipManagers}
                  label="Team Lead"
                  placeholder="Select user"
                  id="primary_contact_id"
                  name="primary_contact_id"
                  search
                  users
                  value={values.primary_contact_id}
                  onChange={(value) => {
                    setFieldValue("primary_contact_id", value);
                  }}
                />
                <Select
                  options={relationShipManagers}
                  label="Lead Owner"
                  placeholder="Select user"
                  id="contract_negotiation"
                  name="contract_negotiation"
                  search
                  users
                  value={values.contract_negotiation}
                  onChange={(value) => {
                    setFieldValue("contract_negotiation", value);
                  }}
                />
                <Select
                  options={relationShipManagers}
                  label="UW"
                  placeholder="Select user"
                  id="uw_id"
                  name="uw_id"
                  search
                  users
                  value={values.uw_id}
                  onChange={(value) => {
                    setFieldValue("uw_id", value);
                  }}
                />                
                {isPCAdmin && (
                  <Select
                    label="Entity"
                    placeholder="Select user"
                    id="ems_entity"
                    name="ems_entity"
                    options={[
                      { label: "PC", value: "pc" },
                      { label: "EMS", value: "ems" },
                      { label: "PaymentNerds", value: "paymentnerds" },
                      { label: "Paychex", value: "paychex" },
                      { label: "KurvPay", value: "kurvpay" },
                    ]}
                    value={values.ems_entity}
                    onChange={(value) => {
                      setFieldValue("ems_entity", value);
                    }}
                  />
                )}
              </div>

              {isPCAdmin && (
                <div
                  className={`${styles.grid} ${styles.gridCols3} ${styles.gap2}`}
                >
                  <Select
                    label="Kurv Office HR"
                    placeholder="Select office"
                    options={officeOptionsForRisk(
                      emsOffices,
                      "high_risk",
                      values?.ems_office_hr,
                    )}
                    id="ems_office_hr"
                    name="ems_office_hr"
                    search
                    value={values?.ems_office_hr?.toString()}
                    onChange={(value) => {
                      setFieldValue("ems_office_hr", value);
                    }}
                  />

                  <Select
                    label="Kurv Office LR"
                    placeholder="Select office"
                    options={officeOptionsForRisk(
                      emsOffices,
                      "low_risk",
                      values?.ems_office_lr,
                    )}
                    id="ems_office_lr"
                    name="ems_office_lr"
                    search
                    value={values?.ems_office_lr?.toString()}
                    onChange={(value) => {
                      setFieldValue("ems_office_lr", value);
                    }}
                  />
                </div>
              )}
              {isEntitySuperAdmin && (
                <TagInput
                  label="Offices"
                  id="offices"
                  name="offices"
                  options={emsOffices}
                  value={values?.offices || []}
                  onChange={(value) => {
                    setFieldValue("offices", value);
                  }}
                  search={true}
                />
              )}
              <TagInput
                id={"additional_team_lead"}
                name={"additional_team_lead"}
                options={relationShipManagers}
                value={relationShipManagers.filter((option) =>
                  values.additional_team_lead?.includes(option.value),
                )}
                onChange={(value) => {
                  const newValues = value.map((option) => option.value);
                  setFieldValue("additional_team_lead", newValues);
                }}
                placeholder={"Select Additional Team Lead"}
                search={true}
              />
              <div>
                <label
                  className={`${formBaseStyles.label} ${formBaseStyles.labelLarge}`}
                >
                  Merchant contact preference
                </label>
                <div
                  className={`${styles.flex} ${styles.flexWrap} ${styles.gap1}`}
                >
                  <FancyRadioInput
                    id="contact_preference_ok"
                    name="contact_preference"
                    label="OK to contact"
                    checked={
                      values.contact_preference !== "" &&
                      values.contact_preference == "0"
                    }
                    onChange={(e) => {
                      setFieldValue("contact_preference", "0");
                    }}
                  />
                  <FancyRadioInput
                    id="contact_preference_not_ok"
                    name="contact_preference"
                    label="NOT OK to contact"
                    checked={
                      values.contact_preference !== "" &&
                      values.contact_preference == "1"
                    }
                    onChange={(e) => {
                      setFieldValue("contact_preference", "1");
                    }}
                  />
                </div>
              </div>

              <HeadingWithLine text="Revenue Share" margin={false} />

              <div
                className={`${styles.grid} ${styles.gridCols3} ${styles.gap2}`}
              >
                <TextInput
                  prefix={<PercentIcon />}
                  label="High Risk"
                  id="high_risk"
                  name="high_risk"
                  suffix={<InfoIcon />}
                  defaultValue={values.high_risk}
                  onChange={handleChange}
                  touched={touched.high_risk}
                  errored={errors.high_risk}
                  error={errors["high_risk"]}
                  cleaveOptions={{
                    numericOnly: true,
                    blocks: [3, 2],
                    delimiter: ".",
                    delimiterLazyShow: true,
                    onValueChanged: (e) => {
                      setFieldValue("high_risk", e.target.value);
                    },
                  }}
                />
                <TextInput
                  prefix={<PercentIcon />}
                  label="Low Risk"
                  id="low_risk"
                  name="low_risk"
                  suffix={<InfoIcon />}
                  defaultValue={values.low_risk}
                  onChange={handleChange}
                  touched={touched.low_risk}
                  errored={errors.low_risk}
                  error={errors["low_risk"]}
                  cleaveOptions={{
                    numericOnly: true,
                    blocks: [3, 2],
                    delimiter: ".",
                    delimiterLazyShow: true,
                    onValueChanged: (e) => {
                      setFieldValue("low_risk", e.target.value);
                    },
                  }}
                />
                <TextInput
                  label="Rev Share Tiers"
                  id="rev_share_tiers"
                  name="rev_share_tiers"
                  suffix={<InfoIcon />}
                  onChange={handleChange}
                  defaultValue={values.rev_share_tiers}
                  touched={touched.rev_share_tiers}
                  errored={errors.rev_share_tiers}
                  error={errors["rev_share_tiers"]}
                  cleaveOptions={{
                    numericOnly: true,
                    blocks: [10],
                    onValueChanged: (e) => {
                      setFieldValue("rev_share_tiers", e.target.value);
                    },
                  }}
                />

                <div>
                  <Switch
                    id="ach_info_for_billing"
                    name="ach_info_for_billing"
                    label="ACH Info for Billing"
                    checked={values.ach_info_for_billing === 1 ? true : false}
                    onChange={(e) => {
                      setFieldValue(
                        "ach_info_for_billing",
                        e.target.checked ? 1 : 0,
                      );
                    }}
                  />
                </div>
              </div>

              <HeadingWithLine text="Bank Details" margin={false} />

              <div
                className={`${styles.grid} ${styles.gridCols3} ${styles.gap2}`}
              >
                <TextInput
                  label="Bank name"
                  id="bank_name"
                  name="bank_name"
                  defaultValue={values.bank_name}
                  onChange={handleChange}
                  touched={touched.bank_name}
                  errored={errors.bank_name}
                  error={errors["bank_name"]}
                />
                <TextInput
                  label="Routing number"
                  id="routing_number"
                  name="routing_number"
                  suffix={<InfoIcon />}
                  defaultValue={values.routing_number}
                  onChange={handleChange}
                  touched={touched.routing_number}
                  errored={errors.routing_number}
                  error={errors["routing_number"]}
                  cleaveOptions={{
                    numericOnly: true,
                    blocks: [10],
                    onValueChanged: (e) => {
                      setFieldValue("routing_number", e.target.value);
                    },
                  }}
                />
                <TextInput
                  label="Account Number"
                  id="account_number"
                  name="account_number"
                  suffix={<InfoIcon />}
                  defaultValue={values.account_number}
                  onChange={handleChange}
                  touched={touched.account_number}
                  errored={errors.account_number}
                  error={errors["account_number"]}
                  cleaveOptions={{
                    numericOnly: true,
                    blocks: [10],
                    onValueChanged: (e) => {
                      setFieldValue("account_number", e.target.value);
                    },
                  }}
                />
              </div>

              <HeadingWithLine text="Permissions" margin={false} />
              <div
                className={`${styles.grid} ${styles.gridCols3} ${styles.gap2}`}
              >
                <Switch
                  id="smtp_enabled"
                  name="smtp_enabled"
                  label="SMTP Enabled"
                  checked={values.smtp_enabled == 1 ? true : false}
                  onChange={(e) => {
                    setFieldValue("smtp_enabled", e.target.checked ? 1 : 0);
                  }}
                />
                <Switch
                  id="enable_agent_training"
                  name="enable_agent_training"
                  label="Enable Agent Training"
                  checked={values?.enable_agent_training === 1 ? true : false}
                  onChange={(e) => {
                    setFieldValue(
                      "enable_agent_training",
                      e.target.checked ? 1 : 0,
                    );
                  }}
                />
                <Switch
                  id="show_create"
                  name="show_create"
                  label="Allow Application Creation"
                  checked={values?.show_create === 1 ? true : false}
                  onChange={(e) => {
                    setFieldValue("show_create", e.target.checked ? 1 : 0);
                  }}
                />
                <Switch
                  id="beta_features"
                  name="beta_features"
                  label="Beta Features"
                  checked={values?.beta_features === 1 ? true : false}
                  onChange={(e) => {
                    setFieldValue("beta_features", e.target.checked ? 1 : 0);
                  }}
                />
                <Switch
                  id="pricing_restricted"
                  name="pricing_restricted"
                  label="Pricing Restricted"
                  checked={values?.pricing_restricted === 1 ? true : false}
                  onChange={(e) => {
                    setFieldValue(
                      "pricing_restricted",
                      e.target.checked ? 1 : 0,
                    );
                  }}
                />
                <Switch
                  id="referral_partner"
                  name="referral_partner"
                  label="Referral Partner"
                  checked={values?.referral_partner === 1 ? true : false}
                  onChange={(e) => {
                    setFieldValue("referral_partner", e.target.checked ? 1 : 0);
                  }}
                />
                {isEntitySuperAdmin && (
                  <Switch
                    id="sync_with_kurv"
                    name="sync_with_kurv"
                    label="Sync With Kurv"
                    checked={values?.sync_with_kurv === 1 ? true : false}
                    onChange={(e) => {
                      setFieldValue("sync_with_kurv", e.target.checked ? 1 : 0);
                    }}
                  />
                )}
                {isEntitySuperAdmin && (
                  <Switch
                    id="enable_personal_guarantor_toggle"
                    name="enable_personal_guarantor_toggle"
                    label="Enable Personal Guarantor Toggle"
                    checked={
                      values?.enable_personal_guarantor_toggle === 1
                        ? true
                        : false
                    }
                    onChange={(e) => {
                      setFieldValue(
                        "enable_personal_guarantor_toggle",
                        e.target.checked ? 1 : 0,
                      );
                    }}
                  />
                )}
              </div>

              {values.smtp_enabled == 1 ? (
                <div
                  className={`${styles.smtpArea} ${styles.grid} ${styles.gap2}`}
                >
                  <div>
                    <HeadingWithLine
                      text="Simple Mail Transfer Protocol"
                      margin={false}
                      line={false}
                    />
                  </div>
                  <div
                    className={`${styles.grid} ${styles.gridCols2} ${styles.gap2}`}
                  >
                    <TextInput
                      label="SMTP Username"
                      id="smtp_username"
                      name="smtp_username"
                      placeholder={"username@mail.com"}
                      autoComplete="off"
                      defaultValue={values.smtp_username}
                      onChange={handleChange}
                      touched={touched.smtp_username}
                      errored={errors.smtp_username}
                      error={errors["smtp_username"]}
                    />
                    <TextInput
                      type="password"
                      label="SMTP Password"
                      id="smtp_password"
                      name="smtp_password"
                      placeholder="SMTP Password"
                      autoComplete="off"
                      defaultValue={values.smtp_password}
                      onChange={handleChange}
                      touched={touched.smtp_password}
                      errored={errors.smtp_password}
                      error={errors["smtp_password"]}
                    />
                  </div>
                  <div
                    className={`${styles.grid} ${styles.gridCols3} ${styles.gap2}`}
                  >
                    <TextInput
                      label="SMTP Server"
                      id="smtp_server"
                      name="smtp_server"
                      defaultValue={values.smtp_server}
                      onChange={handleChange}
                      touched={touched.smtp_server}
                      errored={errors.smtp_server}
                      error={errors["smtp_server"]}
                    />
                    <Select
                      label="SMTP Port"
                      placeholder="Select port"
                      id="smtp_port"
                      name="smtp_port"
                      options={[
                        { label: "465", value: "465" },
                        { label: "587", value: "587" },
                      ]}
                      value={values?.smtp_port}
                      onChange={(value) => {
                        setFieldValue("smtp_port", value);
                      }}
                    />
                    <Select
                      label="SMTP Encryption"
                      id="smtp_encryption"
                      name="smtp_encryption"
                      placeholder="Select encryption"
                      options={[
                        { label: "SSL", value: "SSL" },
                        { label: "TLS", value: "TLS" },
                      ]}
                      value={values?.smtp_encryption}
                      onChange={(value) => {
                        setFieldValue("smtp_encryption", value);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>

            <div className={`${styles.grid} ${styles.gap2}`}>
              <div
                className={`${styles.flex} ${styles.flexEnd} ${styles.gap1}`}
                style={{ marginTop: "16px" }}
              >
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeModal}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
    <ConfirmationModal
      open={showDiscardConfirm}
      onClose={() => setShowDiscardConfirm(false)}
      onConfirmClick={handleDiscardConfirm}
      message="You have unsaved changes. Discard them?"
      submitButtonText="Discard"
    />
    </>
  );
};

export default AddOrganizationModal;
